import Acid from '../icon/Acid';
import ClimateChange from '../icon/ClimateChange';
import Field from '../icon/Field';
import Fog from '../icon/Fog';
import Fuel from '../icon/Fuel';
import OzoneLayer from '../icon/OzoneLayer';
import PickAxe from '../icon/PickAxe';
import Plant from '../icon/Plant';
import Radiation from '../icon/Radiation';
import River from '../icon/River';
import Sea from '../icon/Sea';
import Virus from '../icon/Virus';
import Equation from '../icon/Equation';
import ImpactElement from './ImpactElement';
import { useState } from 'react';
import Butterfly from '../icon/Butterfly';
import FreshWater from '../icon/FreshWater';
import Tumor from '../icon/Tumor';
import Anatomy from '../icon/Anatomy';
import DropletAlert from '../icon/DropletAlert';

export default function ExplicationsContent() {
  const [active, setActive] = useState<number>(-1);

  return (
    <div className="flex flex-col items-center justify-center pl-4 md:pl-0 pt-0 xl:pt-24 py-16 lg:py-8">
      <div className="bg-blackOverlay dark:bg-whiteOverlay rounded-2xl md:p-6 p-4">
        <div
          className="flex justify-between cursor-pointer"
          onClick={() => (active === 0 ? setActive(-1) : setActive(0))}
        >
          <h3 className="md:text-2xl text-lg xshort:text-lg font-medium">
            Calcul du score de coût environnemental
          </h3>
          <svg
            className="w-8 "
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <div
          className={
            'md:px-6 flex flex-col gap-4 overflow-hidden transition-all duration-500 ease-in-out ' +
            (active === 0 ? 'max-h-[1000px]' : 'max-h-0')
          }
        >
          <p className="mt-8">
            Il s'agit de la somme pondérée des impacts normalisés.
          </p>
          <Equation />
          <p>
            La note environnementale que nous fournissons repose sur la méthode
            de calcul du score ADEME proposé par Ecobalyse.
          </p>
          <p>
            Il s'agit d'un impact agrégé, c'est à dire la somme des impacts
            normalisés et pondérés de chaque catégorie d'impact selon la methode
            Ecobalyse, incluant l'impact sur la biodiversité. Il se mesure en
            micro-points, sachant qu’un point correspond à l’impact total d’un
            européen sur une année.
          </p>
          <p>
            Les produits sont triés par score et répartis à part égale (en
            fonction du nombre de produits) dans chaque quartile représentait
            ici par notre notation (A à F). La couleur dépend du score obtenu
            par le produit. Plus il est élevé, plus il pollue et s’approche de F
            et du rouge, plus il est faible, moins il pollue et s’approche de A
            et du vert.
          </p>
          <p>
            Un bouton vous permets de voir votre notation de deux façons
            différentes, soit :
          </p>
          <ul className="list-disc list-inside px-6">
            <li className="mb-4">
              « toutes catégories » (choix par défaut) : le périmètre de la
              notation inclura l’ensemble des catégories produits
            </li>
            <li>
              par catégorie : le périmètre de la notation inclura seulement la
              catégorie où se trouve le produit (ex : un Jean sera comparé avec
              tous les autres produits de la catégorie « pantalon »)
            </li>
          </ul>
        </div>
      </div>
      <div className="bg-blackOverlay dark:bg-whiteOverlay rounded-2xl md:p-6 p-4">
        <div
          className="flex justify-between cursor-pointer"
          onClick={() => (active === 1 ? setActive(-1) : setActive(1))}
        >
          <h3 className="md:text-2xl text-lg xshort:text-lg font-medium">
            Pourquoi avons-nous choisi ce système de notation ?
          </h3>
          <svg
            className="w-8 "
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <div
          className={
            'md:px-6 flex flex-col gap-4 overflow-hidden transition-all duration-500 ease-in-out ' +
            (active === 1 ? 'max-h-[1000px]' : 'max-h-0')
          }
        >
          <p className="mt-8">
            Nous avons choisis ce système de notation car c’est la méthodologie
            qui a été le plus éprouvée. Elle prend en compte 16 indicateurs
            environnementaux basés sur le référentiel ADEME. Elle s’appuie sur
            une base de données robuste (Base Empreinte®), qui fournit des
            valeurs environnementales précises pour différents matériaux et
            processus.
          </p>
          <p>
            En somme, cette méthode offre un cadre structuré et scientifiquement
            fondé pour évaluer et communiquer de manière transparente sur
            l’impact environnemental des produits textiles.
          </p>
        </div>
      </div>
      <div className="bg-blackOverlay dark:bg-whiteOverlay rounded-2xl md:p-6 p-4">
        <div
          className="flex justify-between cursor-pointer"
          onClick={() => (active === 2 ? setActive(-1) : setActive(2))}
        >
          <h3 className="md:text-2xl text-lg xshort:text-lg font-medium">
            Fiabilité du score
          </h3>
          <svg
            className="w-8 "
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <div
          className={
            'md:px-6 flex flex-col gap-4 overflow-hidden transition-all duration-500 ease-in-out ' +
            (active === 2 ? 'max-h-[1000px]' : 'max-h-0')
          }
        >
          <p className="mt-8">
            Il est important de prendre en compte la fiabilité à chaque fois que
            vous regardez un score, car un score élevé (et donc soi-disant
            polluant) avec une fiabilité faible ne veut pas obligatoirement dire
            que le produit est polluant. Cela indique seulement que nous
            manquons de données pour vous donner un score précis et que nous
            comblons les informations absentes par :
          </p>
          <ul className="list-disc list-inside px-6">
            <li className="mb-4">
              Le cas le plus défavorable si aucune similarité n’est trouvée
            </li>
            <li>
              Par une similarité si notre Intelligence Artificielle en détecte
              une
            </li>
          </ul>
          <p>
            Donc fournir des données de qualité peut influencer le score de deux
            manières :
          </p>
          <ul className="list-disc list-inside px-6">
            <li className="mb-4">
              De manière positive si l’option est moins polluante
            </li>
            <li>De manière neutre si l’option est identique</li>
          </ul>
          <p>
            En aucun cas l’ajout de données ne peut avoir un effet négatif sur
            le score.
          </p>
        </div>
      </div>
      <div className="bg-blackOverlay dark:bg-whiteOverlay rounded-2xl md:p-6 p-4">
        <div
          className="flex justify-between cursor-pointer"
          onClick={() => (active === 3 ? setActive(-1) : setActive(3))}
        >
          <h3 className="md:text-2xl text-lg xshort:text-lg font-medium">
            Les impacts
          </h3>
          <svg
            className="w-8 "
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z"
              fill="currentColor"
            />
          </svg>
        </div>

        <div
          className={
            'md:px-6 flex flex-col gap-4 overflow-hidden transition-all duration-500 ease-in-out ' +
            (active === 3 ? 'max-h-[5000px]' : 'max-h-0')
          }
        >
          <div className="h-10"></div>
          <ImpactElement
            title="Acidification (acd)"
            unit="mol H+ eq"
            icon={<Acid />}
          >
            <p>
              Indicateur de l'acidification potentielle des sols et des eaux due
              à la libération de gaz tels que les oxydes d'azote et les oxydes
              de soufre.
            </p>
            <p>
              Cet indicateur se mesure en mol (quantité de matière) d'équivalent
              d'ions hydrogène (H+).
            </p>
          </ImpactElement>
          <ImpactElement
            title="Appauvrissement de la couche d'ozone (ozd)"
            unit="kg CFC-11 eq"
            icon={<OzoneLayer />}
          >
            <p>
              La couche d'ozone est située en haute altitude dans l'atmosphère,
              elle protège des rayons ultra-violets solaires. Son
              appauvrissement augmente l'exposition de l'ensemble des êtres
              vivants à ces radiations négatives (cancérigènes en particulier).
            </p>
            <p>
              Cet indicateur se mesure en kg d'équivalent CFC 11, le CFC 11
              (trichlorofluorométhane) étant l'un des gaz responsable de
              l'appauvrissement de la couche d'ozone.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Changement climatique (cch)"
            unit="kg CO2 eq"
            icon={<ClimateChange />}
          >
            <p>
              Indicateur le plus connu, correspond à la modification du climat,
              affectant l'écosystème global.
            </p>
            <p>
              Cet indicateur se mesure en kg équivalent CO₂, le principal gaz à
              effet de serre.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Eutrophisation eaux douce (fwe)"
            unit="kg P eq"
            icon={<River />}
          >
            <p>
              Indicateur correspondant à un enrichissement excessif des milieux
              naturels en nutriments, ce qui conduit à une prolifération et une
              asphyxie (zone morte). C'est ce phénomène qui est à l'origine des
              algues vertes. On peut le retrouver en rivière et en lac
              également.
            </p>
            <p>
              Cet indicateur se mesure en kg d'équivalent Phosphore (P), le
              phosphore étant l'un des éléments responsables de l'eutrophisation
              des eaux douces.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Eutrophisation marine (swe)"
            unit="kg N eq"
            icon={<Sea />}
          >
            <p>
              Indicateur correspondant à un enrichissement excessif des milieux
              naturels en nutriments, ce qui conduit à une prolifération et une
              asphyxie (zone morte). C'est ce phénomène qui est à l'origine des
              algues vertes.
            </p>
            <p>
              Cet indicateur se mesure en kg d'équivalent azote (N), l'azote
              étant l'un des éléments responsables de l'eutrophisation des eaux
              marines.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Eutrophisation terrestre (tre)"
            unit="mol N eq"
            icon={<Plant />}
          >
            <p>
              Comme dans l'eau, l'eutrophisation terrestre correspond à un
              enrichissement excessif du milieu, en azote en particulier,
              conduisant a un déséquilibre et un appauvrissement de
              l'écosystème. Ceci concerne principalement les sols agricoles.
            </p>
            <p>Cet indicateur se mesure en mol d'équivalent azote (N).</p>
          </ImpactElement>
          <ImpactElement
            title="Formation d'ozone photochimique (pco)"
            unit="kg NMVOC eq"
            icon={<Fog />}
          >
            <p>
              Indicateur correspondant à la dégradation de la qualité de l'air,
              principalement via la formation de brouillard de basse altitude
              nommé smog. Il a des conséquences néfastes sur la santé.
            </p>
            <p>
              Cet indicateur se mesure en kg d'équivalent Composés Organiques
              Volatiles Non Méthaniques (COVNM), un ensemble de composés
              organiques (alcools, aromatiques,...) contribuant à la formation
              d'ozone photochimique.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Particules (pma)"
            unit="disease incidences"
            icon={<Virus />}
          >
            <p>
              Indicateur correspondant aux effets négatifs sur la santé humaine
              causés par les émissions de particules (PM) et de leurs
              précurseurs (NOx, SOx, NH3).
            </p>
            <p>
              La mesure correspond au Particules en Mouvement Actif. elle évalue
              la concentration de particules en suspension dans un fluide, comme
              dans l'analyse de la qualité de l'air ou de l'eau, en particulier
              lorsque les particules sont en mouvement ou en suspension dans un
              écoulement.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Radiations ionisantes (ior)"
            unit="kg Bq-U235 eq"
            icon={<Radiation />}
          >
            <p>
              Indicateur correspondant aux dommages pour la santé humaine et les
              écosystèmes liés aux émissions de radionucléides.
            </p>
            <p>Il se mesure en kilobecquerel d'equivalent Uranium 235.</p>
          </ImpactElement>
          <ImpactElement
            title="Utilisation de ressources fossiles (fru)"
            unit="MJ"
            icon={<Fuel />}
          >
            <p>
              Indicateur de l'épuisement des ressources naturelles en
              combustibles fossiles (gaz, charbon, pétrole).
            </p>
            <p>
              Il se mesure en mégajoules (MJ), la quantité d'énergie fossile
              utilisée.
            </p>
          </ImpactElement>
          <ImpactElement
            title="Utilisation de ressources minérales et métaliques (mru)"
            unit="kg Sb eq"
            icon={<PickAxe />}
          >
            <p>
              Indicateur de l'épuisement des ressources naturelles non fossiles.
            </p>
            <p>
              Il se mesure en kg d'équivalent d'antimoine (Sb) (élément
              métallique).
            </p>
          </ImpactElement>
          <ImpactElement
            title="Utilisation des sols (ldu)"
            unit="pt"
            icon={<Field />}
          >
            <p>
              Mesure de l'évolution de la qualité des sols (production biotique,
              résistance à l'érosion, filtration mécanique).
            </p>
            <p>
              Cet indicateur n'a pas de dimension, il se mesure en Points (Pt).
            </p>
          </ImpactElement>
          <ImpactElement
            title="Biodiversité locale (bvi)"
            unit="pt"
            icon={<Butterfly />}
          >
            <p>
              Mesure la diversité biologique locale en évaluant la variété et
              l'abondance des espèces présentes dans un écosystème donné. Il
              prend en compte plusieurs facteurs, tels que la richesse
              spécifique, l'équité des espèces et la rareté. Cet indicateur aide
              à évaluer et suivre la santé écologique et les impacts
              environnementaux.
            </p>
            <p>
              Cet indicateur n'a pas de dimension, il se mesure en Points (pt).
            </p>
          </ImpactElement>
          <ImpactElement
            title="Écotoxicité de l'eau douce (etf)"
            unit="CTUe"
            icon={<FreshWater />}
          >
            <p>
              Mesure les effets toxiques des substances chimiques sur les
              organismes aquatiques dans les écosystèmes d'eau douce, comme les
              rivières et les lacs.
            </p>
            <p>
              Il se mesure en Concentration-Temps-Unité-d'efficacité (CTUe).
            </p>
          </ImpactElement>
          <ImpactElement
            title="Toxicité humaine - cancer (htc)"
            unit="CTUh"
            icon={<Tumor />}
          >
            <p>
              Mesure le potentiel d'une substance à causer le cancer chez
              l'homme, en estimant le risque sur la base de l'exposition à long
              terme.
            </p>
            <p>Il se mesure en Unité de Toxicité Cancérogène (CTUh).</p>
          </ImpactElement>
          <ImpactElement
            title="Toxicité humaine - non-cancer (htn)"
            unit="CTUh"
            icon={<Anatomy />}
          >
            <p>
              Mesure les effets nocifs d'une substance sur la santé humaine
              autres que le cancer, tels que les effets sur les organes, le
              système nerveux, ou la reproduction.
            </p>
            <p>Il se mesure en Unité de Toxicité Cancérogène (CTUh).</p>
          </ImpactElement>
          <ImpactElement
            title="Utilisation de ressources en eau (wtu)"
            unit="m³"
            icon={<DropletAlert />}
          >
            <p>
              Mesure utilisée pour évaluer l'impact écologique potentiel des
              substances toxiques dans les milieux aquatiques.
            </p>
            <p>Il se mesure en mètre cubes (m³).</p>
          </ImpactElement>
        </div>
      </div>
      {/*
   
  
    //pef: 'PT PEF',
    wtu: 'm³', */}
      <div className="bg-blackOverlay dark:bg-whiteOverlay rounded-2xl md:p-6 p-4">
        <div
          className="flex justify-between cursor-pointer"
          onClick={() => (active === 4 ? setActive(-1) : setActive(4))}
        >
          <h3 className="md:text-2xl text-lg xshort:text-lg font-medium">
            Les produits similaires
          </h3>
          <svg
            className="w-8 "
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.70711 9.71069C5.31658 10.1012 5.31658 10.7344 5.70711 11.1249L10.5993 16.0123C11.3805 16.7927 12.6463 16.7924 13.4271 16.0117L18.3174 11.1213C18.708 10.7308 18.708 10.0976 18.3174 9.70708C17.9269 9.31655 17.2937 9.31655 16.9032 9.70708L12.7176 13.8927C12.3271 14.2833 11.6939 14.2832 11.3034 13.8927L7.12132 9.71069C6.7308 9.32016 6.09763 9.32016 5.70711 9.71069Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div
          className={
            'md:px-6 flex flex-col gap-4 overflow-hidden transition-all duration-500 ease-in-out ' +
            (active === 4 ? 'max-h-[1000px]' : 'max-h-0')
          }
        >
          <p className="mt-8">
            L’Intelligence Artificielle est responsable de la sélection des
            produits similaires. Si vous pensez que celui-ci ne correspond pas à
            vos attentes, n’hésitez pas à nous solliciter sur notre Discord pour
            nous faire part de votre avis.
          </p>
        </div>
      </div>
    </div>
  );
}
